import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import {
  HLM_DIALOG_SHELL,
  HlmAlertComponent,
  HlmBtnDirective,
  HlmCheckboxComponent,
  HlmIconComponent,
  HlmInputDirective,
  HlmNativeSelectDirective,
  HlmSpinnerComponent,
  ToastService,
} from '../../../../ui';
import { Domain, SubBrand, isBareHost, normalizeDomain } from '../domain.model';
import { DomainService } from '../domain.service';
import { SubBrandService } from '../../sub-brands/sub-brand.service';
import { BrandDomainService } from '../brand-domain.service';
import { PermissionService } from '../../../../core/auth/permission.service';
import { mapHttpError } from '../../brand-error.util';

export interface DomainFormDialogData {
  existing: Domain | null;
  /** Brands offered both as the default-brand option list and the per-brand link checkboxes. */
  brands: { id: number; name: string }[];
  linkedBrandIds: number[];
}

const mapError = (err: unknown): string =>
  mapHttpError(err, {
    404: 'That brand no longer exists — pick another.',
    409: 'This domain is already registered.',
    400: 'Invalid domain — use a bare host like audelia.com (no http://, path, or port).',
  });

@Component({
  selector: 'cmt-domain-form-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    HlmBtnDirective,
    HlmCheckboxComponent,
    HlmIconComponent,
    HlmInputDirective,
    HlmNativeSelectDirective,
    HlmSpinnerComponent,
    HlmAlertComponent,
  ],
  templateUrl: './domain-form-dialog.component.html',
})
export class DomainFormDialogComponent {
  readonly data = inject<DomainFormDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<DomainFormDialogComponent>);
  private readonly domainService = inject(DomainService);
  private readonly subBrandService = inject(SubBrandService);
  private readonly brandDomainService = inject(BrandDomainService);
  private readonly permissionService = inject(PermissionService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  /** Read-only users must not reach a mutation even if the dialog was opened. */
  readonly canWrite = computed(() => this.permissionService.canWrite('brandManager'));

  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);

  /** Set of brand IDs this domain should be linked to. Initialised from `data.linkedBrandIds`. */
  readonly selectedBrandIds = signal<Set<number>>(new Set(this.data.linkedBrandIds));

  isLinked(brandId: number): boolean {
    return this.selectedBrandIds().has(brandId);
  }

  toggleBrand(brandId: number): void {
    const next = new Set(this.selectedBrandIds());
    if (next.has(brandId)) {
      next.delete(brandId);
    } else {
      next.add(brandId);
    }
    this.selectedBrandIds.set(next);
  }

  readonly form = inject(FormBuilder).nonNullable.group({
    domain: [
      { value: this.data.existing?.domain ?? '', disabled: this.data.existing != null },
    ],
    mx: [this.data.existing?.mx ?? ''],
    defaultBrand: [this.data.existing?.defaultBrand ?? null as number | null, Validators.required],
    defaultSubBrand: [this.data.existing?.defaultSubBrand ?? null as number | null],
  });

  private readonly subBrands = signal<SubBrand[]>([]);

  private readonly defaultBrandValue = toSignal(this.form.controls.defaultBrand.valueChanges, {
    initialValue: this.form.controls.defaultBrand.value,
  });

  protected readonly subBrandOptions = computed(() =>
    this.subBrands().filter(s => s.parentBrandId === this.defaultBrandValue()),
  );

  constructor() {
    this.subBrandService.listSubBrands()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(subs => {
        this.subBrands.set(subs);
        // Re-assert a valid prefilled sub-brand once its <option>s exist. The
        // native <select>'s SelectControlValueAccessor does not re-sync a value
        // set (in the form initializer) before the options were rendered, so
        // without this the edit picker shows "— none —" instead of the saved
        // sub-brand.
        const current = this.form.controls.defaultSubBrand.value;
        if (current != null && subs.some(s => s.id === current)) {
          this.form.controls.defaultSubBrand.setValue(current);
        }
      });

    // Reset defaultSubBrand when the selected brand changes and the current value
    // is no longer among the available sub-brand options. Guarded on sub-brands
    // having loaded so the initial (empty) option list does not clobber a valid
    // prefill before the data arrives.
    effect(() => {
      if (this.subBrands().length === 0) return;
      const options = this.subBrandOptions();
      const current = this.form.controls.defaultSubBrand.value;
      if (current != null && !options.some(s => s.id === current)) {
        this.form.controls.defaultSubBrand.setValue(null);
      }
    });
  }

  submit(): void {
    // Guard against double-submit (Enter key races the [disabled] flush) and
    // against a read-only user reaching the mutation via a stale/forgotten opener.
    if (this.saving() || !this.canWrite()) return;
    this.errorMessage.set(null);

    // Validate domain format on the CREATE path only. On edit the domain field
    // is disabled and cannot change, so re-validating it there only risks
    // blocking a legitimate edit on a pre-existing record.
    const rawDomain = this.form.getRawValue().domain;
    if (this.data.existing == null && !isBareHost(rawDomain)) {
      this.form.controls.domain.setErrors({ domainFormat: true });
      return;
    }

    // Validate defaultBrand
    if (this.form.controls.defaultBrand.invalid) {
      this.form.controls.defaultBrand.markAsTouched();
      return;
    }

    const domain = normalizeDomain(rawDomain);
    const mx = this.form.controls.mx.value.trim() || null;
    const defaultBrand = this.form.controls.defaultBrand.value as number;
    const defaultSubBrand = this.form.controls.defaultSubBrand.value ?? null;

    this.saving.set(true);

    const request$ = this.data.existing != null
      ? this.domainService.updateDomain(domain, { mx, defaultBrand, defaultSubBrand })
      : this.domainService.createDomain({ domain, mx, defaultBrand, defaultSubBrand });

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.applyBrandLinks(domain),
      error: (err: unknown) => {
        this.saving.set(false);
        this.errorMessage.set(mapError(err));
      },
    });
  }

  /**
   * After the domain record is saved, diff brand memberships and apply adds/removes
   * in parallel. Closes the dialog when done (or immediately if nothing changed).
   */
  private applyBrandLinks(domain: string): void {
    const originalIds = new Set(this.data.linkedBrandIds);
    const selectedIds = this.selectedBrandIds();

    const added = [...selectedIds].filter(id => !originalIds.has(id));
    const removed = [...originalIds].filter(id => !selectedIds.has(id));

    if (added.length === 0 && removed.length === 0) {
      this.dialogRef.close(true);
      return;
    }

    forkJoin([
      ...added.map(id => this.brandDomainService.createBrandDomain(id, { domain, mailDomain: false })),
      ...removed.map(id => this.brandDomainService.deleteBrandDomain(id, domain)),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.dialogRef.close(true),
      error: () => {
        // The domain itself saved; only some brand-link sync calls failed.
        // Closing (with reload) lets the operator reopen from a fresh baseline —
        // staying open would re-diff against the now-stale data.linkedBrandIds.
        this.toastService.open(
          'Domain saved, but updating some brand links failed — reopen to retry.',
          'OK',
          { duration: 6000 },
        );
        this.dialogRef.close(true);
      },
    });
  }

  protected cancel(): void {
    this.dialogRef.close(false);
  }
}
